import { Mountain, Download, Calendar, TrendingUp, MapPin, CloudRain, CheckCircle, AlertCircle } from 'lucide-react';
import FileUpload from './components/FileUpload';
import RouteInfo from './components/RouteInfo';
import RouteMap from './components/RouteMap';
import ElevationChart from './components/ElevationChart';
import AIAnalysis from './components/AIAnalysis';
import POIsPanel from './components/POIsPanel';
import StravaSegments from './components/StravaSegments';
import React, { useState, useRef } from 'react';
import SurfaceBar from './components/SurfaceBar';
import LegalModal from './components/LegalModal';
import TimingStrategy from './components/TimingStrategy';
import NatureAlerts from './components/NatureAlerts';
import HourlyWeatherTimeline from './components/HourlyWeatherTimeline';
import './App.css';

function App() {
  const [analysisData, setAnalysisData] = useState(null);
  const mapRef = useRef(null);
  const [isLegalOpen, setIsLegalOpen] = useState(false);

  const handleFileSelect = (data) => {
    console.log('Dades rebudes del backend:', data);
    console.log('target_datetime:', data.target_datetime);
    setAnalysisData(data);
  };

  const handleReset = () => {
    setAnalysisData(null);
  };

  const handleDownloadGPX = () => {
    if (!analysisData?.enhanced_gpx) return;

    const blob = new Blob([analysisData.enhanced_gpx], { 
      type: 'application/gpx+xml' 
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = analysisData.enhanced_gpx_filename || 'enhanced_route.gpx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
  <div className="container">
    <div className="header-content">
      {/* ESQUERRA: Logo + Títol */}
      <div className="header-left">
        <Mountain size={40} />
        <div>
          <h1>MTB Route Analyzer</h1>
          <p className="header-subtitle">Anàlisi professional de rutes amb IA, OSM i Strava</p>
        </div>
      </div>

      {/* DRETA: Status + Botó */}
      <div className="header-right">
        {analysisData && (
          <>
            {analysisData.strava_user_connected ? (
              <div className="strava-status connected">
                <CheckCircle size={18} />
                <span>Strava connectat</span>
              </div>
            ) : (
              <div className="strava-status disconnected">
                <AlertCircle size={18} />
                <span>Strava no connectat</span>
              </div>
            )}
            <button onClick={handleReset} className="header-new-analysis-button">
              ← Nova ruta
            </button>
          </>
        )}
      </div>
    </div>
  </div>
</header>

      {/* Main Content */}
      <main className="app-main">
        <div className="container">
          
          {/* File Upload Section */}
          {!analysisData && (
            <section className="upload-section">
              <FileUpload onAnalysisComplete={handleFileSelect} />
            </section>
          )}

          {/* Results Section */}
          {analysisData && (
            <>
              {/* Botó Download Sticky */}
              <button onClick={handleDownloadGPX} className="download-button-sticky">
                <Download size={20} />
                Descarregar GPX
              </button>

              {/* Route Info Card */}
              <section className="route-info-section">
                <RouteInfo 
                  filename={analysisData.filename}
                  targetDateTime={analysisData.target_datetime}
                />
              </section>

              {/* Nova Secció d'Alertes */}
              <NatureAlerts 
                alerts={analysisData.nature_alerts} 
                aiSynthesis={analysisData.news_synthesis}
              />
					


{/* Stats Cards */}
<section className="stats-section fade-in">
  <div className="stats-grid">
    <div className="stat-card">
      <div className="stat-icon">
        <MapPin size={24} />
      </div>
      <div className="stat-content">
        <h3>Distància</h3>
        <p className="stat-value">{analysisData.route_stats.distance_km.toFixed(2)} km</p>
      </div>
    </div>

    <div className="stat-card">
      <div className="stat-icon">
        <TrendingUp size={24} />
      </div>
      <div className="stat-content">
        <h3>Desnivell +</h3>
        <p className="stat-value">{analysisData.route_stats.elevation_gain.toFixed(0)} m</p>
      </div>
    </div>

    <div className="stat-card">
      <div className="stat-icon">
        <Calendar size={24} />
      </div>
      <div className="stat-content">
        <h3>Temps estimat</h3>
        <p className="stat-value">{analysisData.ai_analysis.estimated_time}</p>
      </div>
    </div>

    {/* TARGETA DE PREVISIÓ HORÀRIA */}
<div className="stat-card stat-card-weather">
  <div className="stat-header-compact">
    <CloudRain size={20} />
    <h3>Previsió Horària</h3>
  </div>
  
  {/* Previsió horària amb detalls */}
  {analysisData.weather?.hourly_forecast && analysisData.weather.hourly_forecast.length > 0 && (
    <div className="weather-hourly-detailed">
      {analysisData.weather.hourly_forecast.slice(0, 4).map((segment, index) => {
        const getWeatherIcon = (conditions) => {
          const lower = conditions.toLowerCase();
          if (lower.includes('xàfec') || lower.includes('pluja')) return '🌧️';
          if (lower.includes('clar') || lower.includes('sol')) return '☀️';
          if (lower.includes('nuvolós') || lower.includes('ennuvolat')) return '☁️';
          return '🌤️';
        };
        
        return (
          <div key={index} className="hour-detail">
            <span className="hour-time">{segment.hour}</span>
            <span className="hour-icon">{getWeatherIcon(segment.weather.conditions)}</span>
            <span className="hour-temp">{segment.weather.temperature}°C</span>
            {segment.weather.precipitation > 0 && (
              <span className="hour-rain">💧 {segment.weather.precipitation}mm</span>
            )}
            {segment.weather.wind_speed > 10 && (
              <span className="hour-wind">💨 {segment.weather.wind_speed} km/h</span>
            )}
          </div>
        );
      })}
    </div>
  )}
</div>
  </div>
</section>

              {/* Map and Chart - AMB SINCRONITZACIÓ */}
              <section className="visualization-section fade-in">
                <div className="viz-grid">
                  <div className="viz-card map-card">
                    <h2>Mapa de la Ruta</h2>
                    <RouteMap 
                      ref={mapRef}
                      coordinates={analysisData.coordinates}
                      pois={analysisData.pois}
                      stravaSegments={analysisData.strava_segments}
                    />
                  </div>

                  <div className="viz-card chart-card">
                    <h2>Perfil d'Elevació</h2>
                    <ElevationChart 
                      coordinates={analysisData.coordinates}
                      mapRef={mapRef}
                    />
                  </div>
                </div>
              </section>

              {/* Surface Bar */}
              <section className="surface-section fade-in">
                <SurfaceBar surfaceData={analysisData.surface_data} />
              </section>

              {/* AI Analysis */}
              <section className="analysis-section fade-in">
                <TimingStrategy strategy={analysisData.ai_analysis.timing_strategy} />
                <AIAnalysis analysis={analysisData.ai_analysis} />
              </section>

              {/* POIs and Strava */}
              <section className="features-section fade-in">
                <div className="features-grid">
                  <POIsPanel pois={analysisData.pois} />
                  <StravaSegments segments={analysisData.strava_segments} />
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer-compact">
        <div className="container footer-content-minimal">
          <div className="footer-left">
            <Mountain size={18} className="footer-icon" />
            <span className="footer-brand-name">MTB Analyzer</span>
            <span className="footer-divider">|</span>
            <span className="footer-tech-stack">Powered by AI & OSM</span>
          </div>
          
          <div className="footer-center">
            <a href="mailto:jordi@tecnojs.com" className="footer-contact-highlight">
              <span>📧</span> jordi@tecnojs.com
            </a>
          </div>
          
          <div className="footer-right">
            <button onClick={() => setIsLegalOpen(true)} className="footer-legal-link">
              Legal & Credits
            </button>
            <span className="footer-copy">© {new Date().getFullYear()} TecnoJS</span>
          </div>
        </div>
      </footer>

      {/* El Modal */}
      <LegalModal 
        isOpen={isLegalOpen} 
        onClose={() => setIsLegalOpen(false)} 
      />
    </div>
  );
}

export default App;